import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonListHeader,
    IonTitle,
    IonToggle,
    IonToolbar,
    ModalController,
} from '@ionic/angular/standalone';
import { isEqual } from 'lodash-es';
import { arrayMax, arrayMin } from '../../hgm-setup/src/module/util/core';

import { LOGGER } from '../../hgm-setup/src/module/util/logger';
import { DataProvider } from '../../providers/data-provider';
import { SignalGroupProvider, SignalLayoutEntry } from '../../providers/signal-group-provider';
import { SignalSettingsProvider, TraceRange, defaultTraceRange } from '../../providers/signal-settings-provider';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        IonHeader,
        IonToolbar,
        IonButtons,
        IonButton,
        IonTitle,
        IonContent,
        IonItem,
        IonLabel,
        IonListHeader,
        IonToggle,
        IonIcon,
        IonInput,
    ],
    template: `
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-button (click)="onBack()">
                        <ion-icon slot="icon-only" name="close"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title class="ion-text-wrap">{{ signal().name }}</ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content class="ion-padding" scrollY="true">
            <div style="padding-bottom: 2em">
                <ion-list-header>
                    <ion-label>View Bounds</ion-label>
                </ion-list-header>

                <ion-item>
                    <ion-label>Dedicated Y Axis</ion-label>
                    <ion-toggle mode="ios" slot="end" [(ngModel)]="dedicatedY"></ion-toggle>
                </ion-item>

                <div style="display: flex; justify-content: space-between; padding: 0 16px 8px 16px">
                    <ion-button (click)="setActualRange()" size="small" fill="clear">
                        <ion-icon slot="start" name="refresh"></ion-icon>
                        ACTUAL
                    </ion-button>
                    <ion-button (click)="resetRange()" size="small" fill="clear">
                        <ion-icon slot="start" name="refresh" style="transform: scaleX(-1)"></ion-icon>
                        Defaults
                    </ion-button>
                </div>

                <ion-item>
                    <ion-input
                        label="Min"
                        labelPlacement="start"
                        type="number"
                        class="ion-text-right"
                        [(ngModel)]="minY"
                        placeholder="Auto"
                    ></ion-input>
                    <div slot="end" class="unit-label" style="min-width: 30px; text-align: left" [innerHTML]="unit()"></div>
                </ion-item>

                <ion-item>
                    <ion-input
                        label="Max"
                        labelPlacement="start"
                        type="number"
                        class="ion-text-right"
                        [(ngModel)]="maxY"
                        placeholder="Auto"
                    ></ion-input>
                    <div slot="end" class="unit-label" style="min-width: 30px; text-align: left" [innerHTML]="unit()"></div>
                </ion-item>
            </div>
        </ion-content>
    `,
    styles: `
        .value-edit {
            display: flex;
            flex-direction: row;
            align-items: baseline;
            flex-grow: 1;
            flex-basis: 0;
        }
        ion-item {
            --highlight-background: transparent !important;
        }
        ion-label {
            flex-shrink: 0;
        }
    `,
})
export class EditSignalComponent implements OnInit {
    readonly signal = input.required<SignalLayoutEntry>();
    readonly unit = computed(() => this.dataProvider.data()?.signals[this.signal().key]?.unit || '');

    private range: TraceRange = { ...defaultTraceRange };
    private readonly dataProvider = inject(DataProvider);
    private readonly modalCtrlr = inject(ModalController);
    private readonly signalGroupProvider = inject(SignalGroupProvider);
    private readonly signalSettingsProvider = inject(SignalSettingsProvider);
    private readonly logger = inject(LOGGER);

    ngOnInit() {
        this.range = {
            ...(this.signalSettingsProvider.ranges[this.signal().key] || defaultTraceRange),
        };
    }

    deleteSignal() {
        this.modalCtrlr.dismiss().catch((err) => this.logger.warn(err));
        this.signalGroupProvider.layout = this.signalGroupProvider.layout.filter((e) => e !== this.signal());
    }

    resetRange() {
        this.range = { ...defaultTraceRange };
        this.save();
    }

    setActualRange() {
        // Read from the raw, unformatted paramSet to avoid string-encoded DisplaySlot output (e.g., Gear text)
        const rawSignalData = this.dataProvider.result()?.paramSet?.signals[this.signal().key]?.data;
        if (!rawSignalData || rawSignalData.length === 0) return;

        const yValues = rawSignalData.map((d) => d[1]).filter((y) => isFinite(y));
        if (yValues.length > 0) {
            this.range.minY = arrayMin(yValues);
            this.range.maxY = arrayMax(yValues);
        }
        this.save();
    }

    onBack() {
        this.modalCtrlr.dismiss().catch((err) => this.logger.warn(err));
    }

    get minY() {
        const engr = this.signal().slot.toEngrFloat(this.range.minY);
        return engr !== undefined ? Number(engr.toFixed(this.signal().slot.precision)).toString() : '';
    }

    set minY(v) {
        if (v === '' || v === undefined || v === null) {
            this.range.minY = undefined;
        } else {
            const raw = this.signal().slot.toRawFloat(v);
            this.range.minY = Object.is(raw, NaN) ? parseFloat(v as string) : raw;
        }
        this.save();
    }

    get maxY() {
        const engr = this.signal().slot.toEngrFloat(this.range.maxY);
        return engr !== undefined ? Number(engr.toFixed(this.signal().slot.precision)).toString() : '';
    }

    set maxY(v) {
        if (v === '' || v === undefined || v === null) {
            this.range.maxY = undefined;
        } else {
            const raw = this.signal().slot.toRawFloat(v);
            this.range.maxY = Object.is(raw, NaN) ? parseFloat(v as string) : raw;
        }
        this.save();
    }

    get dedicatedY() {
        return this.range.dedicatedY || false;
    }

    set dedicatedY(v: boolean) {
        this.range.dedicatedY = v;
        this.save();
    }

    private save() {
        if (!isEqual(this.signalSettingsProvider.ranges[this.signal().key], this.range)) {
            this.signalSettingsProvider.setRange(this.signal().key, this.range);
        }
    }
}
